import { keyframes } from '@emotion/react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  IconButton,
  InputBase,
  Modal,
  styled,
  Typography,
} from '@mui/material';

/* ── Modals (activation + deactivate) ─────────────────────────────────────── */

export const KioskModalStyled = styled(Modal)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '550px',
  maxHeight: '90vh',
  height: 'auto',
  // Intentionally NOT overriding the default modal z-index (~1300): nested Select popovers are
  // stacked above the modal by MUI's modal manager only when the modal sits at the default level.
  '.MuiBackdrop-root': {
    boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
    position: 'relative',
  },
  [theme.breakpoints.up('sm')]: {
    width: '550px',
  },
  [theme.breakpoints.down(611)]: {
    width: '90%',
  },
}));

export const KioskModalContainer = styled(Box)(() => ({
  width: '100%',
  background: 'white',
  padding: '32px',
  boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
  borderRadius: '8px',
  border: 'none',
  maxHeight: '90vh',
  overflowY: 'auto',
}));

export const KioskModalTitle = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 'bold',
  color: theme.palette.secondary.contrastText,
  marginBottom: '8px',
}));

export const KioskModalSubtitle = styled(Typography)(() => ({
  fontSize: 14,
  color: '#6B6B6B',
  marginBottom: '24px',
}));

export const KioskFieldContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

export const KioskFieldHint = styled(Typography)(() => ({
  fontSize: 12,
  color: '#6B6B6B',
  marginTop: '-8px',
}));

export const KioskErrorMessage = styled(Typography)(({ theme }) => ({
  marginTop: '16px',
  fontSize: 14,
  color: theme.palette.error.main,
}));

export const KioskNoPlanningMessage = styled(Typography)(({ theme }) => ({
  padding: '24px 0',
  fontSize: 16,
  fontWeight: 'bold',
  textAlign: 'center',
  color: theme.palette.secondary.contrastText,
}));

export const KioskModalActions = styled(Box)(() => ({
  marginTop: '32px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '16px',
}));

export const KioskEmptyState = styled(Typography)(() => ({
  fontSize: 14,
  color: '#6B6B6B',
  textAlign: 'center',
  padding: '16px 0',
}));

/* ── Selection screen (US 7346) ───────────────────────────────────────────── */

export const KIOSK_SELECTION_TOKENS = {
  gradient: 'linear-gradient(180deg, #ffffff 0%, #f6fcf7 30%, #e3f3e7 62%, #d8eedc 100%)',
  accent: '#16a35c',
  textPrimary: '#20303f',
  textMuted: '#8c9aa6',
  textSubtle: '#7c8a94',
  sectionHeader: '#28384a',
  cardBg: '#ffffff',
  cardShadow: '0 2px 12px rgba(31,58,42,0.06)',
  metaIcon: '#5a6b78',
  chromeIcon: '#46586a',
  chromeBg: 'rgba(255,255,255,0.72)',
  searchBorder: '#e4ece6',
};

// `scrollable` is off while the PIN stage is mounted: MUI's Zoom animates via `transform`, and a
// transformed descendant temporarily extends an `overflow: auto` ancestor's scrollable area, which
// flashed a scrollbar (and reflowed the centered numpad) for the duration of the animation. Fade-based
// views never hit this since opacity transitions don't affect the scrollable overflow box.
export const KioskSelectionContainer = styled(Box)<{ scrollable?: boolean }>(({ scrollable = true }) => ({
  position: 'fixed',
  inset: 0,
  overflowY: scrollable ? 'auto' : 'hidden',
  background: KIOSK_SELECTION_TOKENS.gradient,
  // Keep low so the deactivate modal (default MUI z-index ~1300) floats above the screen.
  zIndex: 1,
}));

// Decorative leaf illustration (mirrors the mobile app's kiosk background), rendered once and left
// mounted across every stage — only its opacity toggles, so it never remounts/flickers when the
// feed swaps for the PIN pad or a registration form. Rendered first so later siblings (header,
// stage content) paint above it without needing an explicit z-index.
export const KioskBackgroundImage = styled('img')<{ visible?: boolean }>(({ visible }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  height: '100%',
  objectFit: 'cover',
  pointerEvents: 'none',
  opacity: visible ? 1 : 0,
  transition: 'opacity 340ms ease',
}));

// Top padding leaves room for the persistent KioskHeaderRow (fixed, top:40px, ~46px tall) which
// floats above every stage now instead of living in each view's own flow — see KioskHeaderRow.
export const KioskSelectionInner = styled(Box)(() => ({
  maxWidth: '1440px',
  minHeight: '100%',
  margin: '0 auto',
  padding: '100px 56px 48px',
  boxSizing: 'border-box',
  position: 'relative',
}));

// Persistent chrome: rendered once at the KioskSelectionScreen level, outside every per-stage
// Fade/Zoom, so it never re-animates or remounts on stage transitions — only its own children
// (the reload button's Fade, the action icon swap) change. `position: fixed` keeps it pinned
// through scrolling; the max-width/centering mirrors KioskSelectionInner/KioskStageWrap so the
// buttons land on the exact same column edges the in-flow header used to occupy.
export const KioskHeaderRow = styled(Box)(() => ({
  position: 'fixed',
  top: '40px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '100%',
  maxWidth: '1440px',
  padding: '0 56px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  zIndex: 3,
}));

export const KioskChromeButton = styled(IconButton)(() => ({
  width: '46px',
  height: '46px',
  borderRadius: '50%',
  background: KIOSK_SELECTION_TOKENS.chromeBg,
  boxShadow: '0 2px 8px rgba(31,58,42,0.08)',
  color: KIOSK_SELECTION_TOKENS.chromeIcon,
  '&:hover': { background: 'rgba(255,255,255,0.9)' },
}));

// Header language selector: a pill matching the chrome buttons, one segment per language.
export const KioskHeaderActions = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

export const KioskLanguageGroup = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  height: '46px',
  padding: '4px',
  boxSizing: 'border-box',
  borderRadius: '23px',
  background: KIOSK_SELECTION_TOKENS.chromeBg,
  boxShadow: '0 2px 8px rgba(31,58,42,0.08)',
}));

export const KioskLanguageOption = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  minWidth: '46px',
  height: '38px',
  padding: '0 12px',
  borderRadius: '19px',
  fontSize: 15,
  fontWeight: active ? 800 : 600,
  letterSpacing: '0.4px',
  color: active ? '#ffffff' : KIOSK_SELECTION_TOKENS.chromeIcon,
  background: active ? KIOSK_SELECTION_TOKENS.accent : 'transparent',
  transition: 'background 150ms ease, color 150ms ease',
}));

export const KioskGreetingTitle = styled(Typography)(() => ({
  fontSize: 40,
  fontWeight: 800,
  color: KIOSK_SELECTION_TOKENS.textPrimary,
  letterSpacing: '-0.6px',
  textAlign: 'center',
  margin: 0,
}));

export const KioskGreetingSubtitle = styled(Typography)(() => ({
  fontSize: 17,
  color: KIOSK_SELECTION_TOKENS.textMuted,
  textAlign: 'center',
  marginTop: '8px',
}));

export const KioskSessionName = styled(Typography)(() => ({
  fontSize: 14,
  fontWeight: 600,
  color: KIOSK_SELECTION_TOKENS.textSubtle,
  textAlign: 'center',
  marginTop: '10px',
}));

export const KioskSearchWrapper = styled(Box)<{ active?: boolean }>(({ active }) => ({
  maxWidth: '840px',
  margin: '24px auto 0',
  height: '58px',
  background: '#fff',
  border: active ? `2px solid ${KIOSK_SELECTION_TOKENS.accent}` : `1px solid ${KIOSK_SELECTION_TOKENS.searchBorder}`,
  borderRadius: '16px',
  boxShadow: active ? '0 4px 16px rgba(22,163,92,0.12)' : '0 2px 10px rgba(31,58,42,0.04)',
  display: 'flex',
  alignItems: 'center',
  padding: '0 22px',
  gap: '12px',
  boxSizing: 'border-box',
}));

export const KioskSearchInput = styled(InputBase)(() => ({
  flex: 1,
  fontSize: 16,
  color: KIOSK_SELECTION_TOKENS.textPrimary,
  '& input::placeholder': { color: '#aab4bd', opacity: 1 },
}));

export const KioskMatchCount = styled(Typography)(() => ({
  maxWidth: '840px',
  margin: '12px auto 0',
  fontSize: 13.5,
  color: KIOSK_SELECTION_TOKENS.textSubtle,
  textAlign: 'center',
}));

export const KioskSectionTitle = styled(Typography)(() => ({
  fontSize: 15,
  fontWeight: 700,
  color: KIOSK_SELECTION_TOKENS.sectionHeader,
  margin: '30px 0 14px 4px',
}));

export const KioskCardsGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '14px',
}));

export const KioskCard = styled(Box)<{ clickable?: boolean }>(({ clickable }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: KIOSK_SELECTION_TOKENS.cardBg,
  borderRadius: '16px',
  boxShadow: KIOSK_SELECTION_TOKENS.cardShadow,
  padding: '16px 24px',
  boxSizing: 'border-box',
  minHeight: '58px',
  cursor: clickable ? 'pointer' : 'default',
}));

export const KioskCardName = styled(Typography)(() => ({
  fontSize: 19,
  fontWeight: 700,
  color: KIOSK_SELECTION_TOKENS.textPrimary,
  letterSpacing: '-0.2px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const KioskCardElapsed = styled(Typography)(() => ({
  fontSize: 14,
  color: KIOSK_SELECTION_TOKENS.textMuted,
  letterSpacing: '0.4px',
  marginTop: '3px',
}));

export const KioskCardMeta = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 'none',
  color: KIOSK_SELECTION_TOKENS.metaIcon,
}));

export const KioskCardMetaTime = styled(Typography)(() => ({
  fontSize: 17,
  fontWeight: 600,
  color: '#2b3b4a',
}));

export const KioskEmptyWrap = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '96px',
  textAlign: 'center',
}));

export const KioskEmptyIconCircle = styled(Box)(() => ({
  width: '118px',
  height: '118px',
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: 'inset 0 0 0 1px rgba(31,58,42,0.06)',
  color: '#a9b6bd',
}));

export const KioskEmptyTitle = styled(Typography)(() => ({
  fontSize: 24,
  fontWeight: 700,
  color: '#3c4b58',
  marginTop: '26px',
  maxWidth: '520px',
}));

export const KioskErrorWrap = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '96px',
  textAlign: 'center',
}));

export const KioskErrorText = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 600,
  color: theme.palette.error.main,
  maxWidth: '520px',
}));

/* ── Start-work modal (US 7386a) ──────────────────────────────────────────── */

// Two-column row (matches the mockup's Date | Location and Start | End rows).
// `minWidth: 0` on the cells lets the columns shrink below their content's intrinsic
// width, so long field labels can never force the modal to scroll horizontally.
export const KioskFieldRow = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  alignItems: 'start',
  '& > *': { minWidth: 0 },
}));

export const KioskPoliciesAccordion = styled(Accordion)(() => ({
  border: '1px solid #e6eaec',
  borderRadius: '8px',
  background: '#fafbfb',
  boxShadow: 'none',
  '&:before': { display: 'none' }, // remove MUI's default top divider line
  '&.Mui-expanded': { margin: 0 },
}));

export const KioskPoliciesSummary = styled(AccordionSummary)(({ theme }) => ({
  minHeight: 'unset',
  padding: '0 16px',
  '&.Mui-expanded': { minHeight: 'unset' },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0',
    // ButtonBase renders a native <button>, which resets font-family — restore the theme font (Montserrat).
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.secondary.contrastText,
  },
  '& .MuiAccordionSummary-content.Mui-expanded': { margin: '12px 0' },
  '& .MuiAccordionSummary-expandIconWrapper': { color: KIOSK_SELECTION_TOKENS.accent },
}));

export const KioskPoliciesDetails = styled(AccordionDetails)(() => ({
  padding: '0 16px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}));

export const KioskPoliciesIntro = styled(Typography)(() => ({
  fontSize: 13,
  color: '#6B6B6B',
}));

export const KioskPolicyText = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.secondary.contrastText,
  position: 'relative',
  paddingLeft: '16px',
  '&:before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '8px',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: KIOSK_SELECTION_TOKENS.accent,
  },
}));

/* ── End-registration modal / break editor (US 7347) ──────────────────────── */

export const KioskBreakEditorContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}));

export const KioskBreaksLabel = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.secondary.contrastText,
}));

// Each break is its own block: a right-aligned "Delete" link above the two time inputs
// (which reuse KioskFieldRow). Mirrors the editable break section in the contract detail panel.
export const KioskBreakItem = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
}));

export const KioskBreakActionRow = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
}));

export const KioskAddBreakRow = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: '4px',
}));

// Dark underlined text link for "Delete" / "Add break" — matches KioskWithoutPlanningLink
// and the contract detail panel's LinkButton (deliberately NOT the green accent colour).
export const KioskInlineLink = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 700,
  color: theme.palette.secondary.contrastText,
  fontFamily: theme.typography.fontFamily,
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  '&:disabled': { opacity: 0.5, cursor: 'default' },
}));

export const KioskBreakHint = styled(Typography)(() => ({
  fontSize: 13,
  color: '#6B6B6B',
}));

export const KioskWarningMessage = styled(Typography)(() => ({
  fontSize: 14,
  color: '#b26a00',
}));

export const KioskSummaryBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  paddingTop: '12px',
  borderTop: '1px solid #e6eaec',
}));

export const KioskSummaryRow = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

export const KioskSummaryLabel = styled(Typography)(() => ({
  fontSize: 14,
  color: '#6B6B6B',
}));

export const KioskSummaryValue = styled(Typography)(({ theme }) => ({
  fontSize: 15,
  fontWeight: 700,
  color: theme.palette.secondary.contrastText,
}));

/* ── PIN entry panel (US 7344) ────────────────────────────────────────────── */

export const KioskPinAvatar = styled(Box)(({ theme }) => ({
  width: '62px',
  height: '62px',
  borderRadius: '50%',
  background: theme.palette.primary.main,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '22px',
  fontWeight: 700,
  fontFamily: theme.typography.fontFamily,
  marginBottom: '8px',
}));

export const KioskPinWelcome = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 700,
  fontSize: '28px',
  color: '#303C42',
}));

export const KioskPinPrompt = styled(Typography)(() => ({
  fontSize: '18px',
  color: '#303C42',
}));

// Mirrors the mobile app's shake sequence: 6 damped translateX steps totalling 390ms.
const kioskPinShake = keyframes`
  0% { transform: translateX(0); }
  15.38% { transform: translateX(-10px); }
  33.33% { transform: translateX(9px); }
  51.28% { transform: translateX(-7px); }
  69.23% { transform: translateX(5px); }
  84.62% { transform: translateX(-3px); }
  100% { transform: translateX(0); }
`;

export const KioskPinDotsRow = styled(Box)<{ shaking?: boolean }>(({ shaking }) => ({
  display: 'flex',
  gap: '10px',
  margin: '18px 0 28px',
  animation: shaking ? `${kioskPinShake} 390ms ease` : 'none',
}));

// Mirrors the mobile app's sequential "hop" loading wave: each dot lifts 7px and lands, staggered
// 110ms apart, then holds until the last dot lands and the wave loops — see HOP_STAGGER_MS /
// HOP_REST_MS in the mobile KioskPinDots.tsx for the timing this is ported from.
const kioskPinDotHop = keyframes`
  0% { transform: translateY(0); animation-timing-function: ease-out; }
  19.23% { transform: translateY(-7px); animation-timing-function: ease-in; }
  43.59% { transform: translateY(0); }
  100% { transform: translateY(0); }
`;

export const KioskPinDot = styled(Box)<{ filled?: boolean; hopping?: boolean; hopDelayMs?: number }>(
  ({ theme, filled, hopping, hopDelayMs = 0 }) => ({
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    boxSizing: 'border-box',
    border: `1.5px solid ${theme.palette.primary.main}`,
    background: filled ? theme.palette.primary.main : 'transparent',
    animation: hopping ? `${kioskPinDotHop} 780ms infinite` : 'none',
    animationDelay: hopping ? `${hopDelayMs}ms` : '0ms',
  }),
);

export const KioskPinFeedback = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.error.main,
  minHeight: '20px',
}));

// Fixed-px columns (not 1fr) so KioskNumpadLastRow below can share the exact same column
// positions — a 1fr grid sizes each column to its own row's content, which is how the digit
// and backspace mis-aligned with the grid above it.
export const KioskNumpadGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 62px)',
  gap: '12px',
  justifyContent: 'center',
}));

export const KioskNumpadLastRow = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 62px)',
  gap: '12px',
  alignItems: 'center',
  justifyItems: 'center',
}));

export const KioskNumpadButton = styled(KioskChromeButton)(({ theme }) => ({
  width: '62px',
  height: '62px',
  background: theme.palette.primary.main,
  color: '#fff',
  fontFamily: theme.typography.fontFamily,
  fontSize: '24px',
  fontWeight: 500,
  '&:hover': { background: theme.palette.primary.main },
  '&.Mui-disabled': { opacity: 0.5, color: '#fff' },
}));

export const KioskNumpadBackspaceButton = styled(IconButton)(() => ({
  color: KIOSK_SELECTION_TOKENS.metaIcon,
}));

/* ── Stage views (single-screen flow) ─────────────────────────────────────── */

// Root of every non-feed stage view. Padding mirrors KioskSelectionInner, including the
// bumped top inset that leaves room for the persistent KioskHeaderRow floating above it.
export const KioskStageWrap = styled(Box)(() => ({
  maxWidth: '1440px',
  minHeight: '100%',
  margin: '0 auto',
  padding: '100px 56px 48px',
  boxSizing: 'border-box',
  position: 'relative',
}));

// Vertically centers the PIN content in the viewport (100vh minus KioskStageWrap's vertical padding).
export const KioskStageCentered = styled(Box)(() => ({
  minHeight: 'calc(100vh - 148px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  textAlign: 'center',
}));

export const KioskStageTitle = styled(Typography)(() => ({
  fontSize: 32,
  fontWeight: 800,
  color: KIOSK_SELECTION_TOKENS.textPrimary,
  letterSpacing: '-0.4px',
  textAlign: 'center',
  marginTop: '12px',
}));

export const KioskStageSubtitle = styled(Typography)(() => ({
  fontSize: 17,
  color: KIOSK_SELECTION_TOKENS.textMuted,
  textAlign: 'center',
  marginTop: '6px',
}));

// The transparent form column for clock-in / clock-out.
export const KioskStageFormColumn = styled(Box)(() => ({
  maxWidth: '680px',
  margin: '32px auto 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

export const KioskStageActions = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '24px',
}));

export const KioskDiscardActionButton = styled(ButtonBase)(() => ({
  alignSelf: 'flex-end',
  marginTop: '12px',
  padding: 0,
  gap: '8px',
  borderRadius: '4px',
  '&.Mui-disabled': { opacity: 0.5 },
}));

export const KioskDiscardActionText = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: '20px',
  color: theme.palette.secondary.contrastText,
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  whiteSpace: 'nowrap',
}));
